/**
 * {@link DataTable} for sport clubs.
 */
import * as React from 'react';
import { Trophy } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { DeleteModal } from '@/components/common/modals/DeleteModal';
import { DataTable } from '@/components/common/data-table';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { FilterConfigurePanel } from '@/components/common/data-table/FilterConfigurePanel';
import { Button } from '@/components/forms/Button';
import { SportClubRow } from './SportClubRow';
import { SportClubCard } from './SportClubCard';
import { SportClubFiltersContent } from './SportClubFilters';
import { renderSportClubDetails } from './renderSportClubDetails';
import { useDataTable } from '@/hooks/useDataTable';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import {
  listSportClubs,
  getSportClubById,
  deleteSportClub,
  exportSportClubsExcel,
} from '@/services/sport-clubs.service';
import { cn } from '@/lib/utils';
import type { SportClub } from '@/types/sport-club.types';
import type { ElementWidth } from '@/types/interface-elements.types';
import {
  ALL_SPORT_CLUB_FILTER_GROUP_IDS,
  DEFAULT_SPORT_CLUB_FILTERS,
  SPORT_CLUB_FILTER_GROUP_LABELS,
  type SportClubFilterGroupId,
  type SportClubFilters,
  type SportClubPageActions,
} from './sport-club-configs';
import {
  formatSportClubDeleteModalItemName,
  getSportClubExpandedColSpan,
  getSportClubExportTooltip,
  hasSportClubActiveFilters,
  isSportClubEmptyForExport,
  sportClubsKeys,
  toSportClubApiFilterParams,
  toSportClubListParams,
} from './sport-club-table-utils';

/** Props for {@link SportClubsDataTable}. */
export interface SportClubsDataTableProps {
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  pageActions?: SportClubPageActions;
  onView?: (c: SportClub) => void;
  onEdit?: (c: SportClub) => void;
  isSection?: boolean;
  layoutWidth?: ElementWidth;
  showCreateButton?: boolean;
  onCreate?: () => void;
  toolbarLeft?: React.ReactNode;
}

/** Sport clubs list / grid with filters, details, delete, and Excel export. */
export const SportClubsDataTable: React.FC<SportClubsDataTableProps> = ({
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  pageActions,
  onView,
  onEdit,
  isSection = false,
  layoutWidth = 'full',
  showCreateButton,
  onCreate,
  toolbarLeft,
}) => {
  const queryClient = useQueryClient();
  const showExport = pageActions?.canExportExcel === true;
  const resolvedView = onView ?? (onToggleRow ? (c: SportClub) => onToggleRow(c.id) : undefined);

  const dt = useDataTable<SportClubFilters, SportClubFilterGroupId>({
    tableName: 'sport-club',
    defaultFilters: DEFAULT_SPORT_CLUB_FILTERS,
    allFilterGroupIds: ALL_SPORT_CLUB_FILTER_GROUP_IDS,
    layoutWidth,
    isSection,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
  });

  const queryParams = React.useMemo(
    () => toSportClubListParams(dt.filters, dt.currentPage, dt.itemsPerPage),
    [dt.filters, dt.currentPage, dt.itemsPerPage],
  );

  const { data: clubsResponse, isLoading } = useQuery({
    queryKey: sportClubsKeys.list(queryParams),
    queryFn: () => listSportClubs(queryParams),
  });

  const clubs = clubsResponse?.data ?? [];
  const totalItems = clubsResponse?.total ?? 0;
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = startIndex + dt.itemsPerPage;

  const expandedClubId = dt.expandedRowId ?? '';
  const {
    data: expandedClubData,
    isLoading: isLoadingExpandedClub,
    isError: isExpandedClubError,
    error: expandedClubError,
  } = useQuery({
    queryKey: sportClubsKeys.detail(expandedClubId),
    queryFn: () => getSportClubById(expandedClubId),
    enabled: !!dt.expandedRowId,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isExpandedClubError,
    error: expandedClubError,
    catalogKey: 'sportClubNotFound',
    onClose: onCloseDetails,
    entityId: dt.expandedRowId,
  });

  const expandedItem = expandedClubData ?? null;

  const deleteClubMutation = useMutation({
    mutationFn: (id: string) => deleteSportClub(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sportClubsKeys.all });
      dt.setDeleteId(null);
      toast.success('Club de sport supprimé avec succès !');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const clubToDelete = React.useMemo(
    () => clubs.find((c) => c.id === dt.deleteId) ?? null,
    [clubs, dt.deleteId],
  );

  const hasActiveFilters = React.useMemo(() => hasSportClubActiveFilters(dt.filters), [dt.filters]);

  const [isExporting, setIsExporting] = React.useState(false);
  const isEmptyForExport = isSportClubEmptyForExport(isLoading, totalItems);

  /** Excel export of the current filter set (blocked when the list is empty). */
  const handleExportExcel = React.useCallback(async () => {
    if (isEmptyForExport) {
      toast.warning(getErrorMessage('exportEmpty'));
      return;
    }
    setIsExporting(true);
    try {
      await exportSportClubsExcel(toSportClubApiFilterParams(dt.filters));
      toast.success('Export Excel réussi !');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  }, [dt.filters, isEmptyForExport]);

  const setSearch = React.useCallback(
    (v: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, search: v }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setCity = React.useCallback(
    (v: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, city: v }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setSportType = React.useCallback(
    (v: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, sportType: v }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const filtersContent = React.useMemo(
    () => (
      <SportClubFiltersContent
        filters={dt.filters}
        allowedFilterIds={dt.allowedFilterIds}
        visibleFilterIds={dt.visibleFilterIds}
        onSetSearch={setSearch}
        onSetCity={setCity}
        onSetSportType={setSportType}
      />
    ),
    [dt.filters, dt.allowedFilterIds, dt.visibleFilterIds, setSearch, setCity, setSportType],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: () => filtersContent,
      },
    ],
    [filtersContent],
  );

  const clearFilters = React.useCallback(() => {
    dt.setFilters(DEFAULT_SPORT_CLUB_FILTERS);
    dt.setCurrentPage(1);
  }, [dt]);

  return (
    <DataTable
      data={clubs}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="clubs"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={getSportClubExpandedColSpan()}
      toolbarActions={
        <>
          <Button
            preset="toolbar-filter"
            className={cn(dt.showFilters && 'section-action-button-active')}
            showBadge={hasActiveFilters}
            onClick={() => {
              const next = !dt.showFilters;
              dt.setShowFilters(next);
              if (!next) dt.setShowConfigure(false);
            }}
          />
          {showExport && (
            <Button
              preset="toolbar-export"
              onClick={handleExportExcel}
              loading={isExporting}
              disabled={isEmptyForExport}
              tooltipText={getSportClubExportTooltip(isEmptyForExport, isExporting)}
            />
          )}
          {showCreateButton && onCreate && (
            <Button
              preset="toolbar-create"
              tooltipText="Créer un club"
              onClick={() => {
                onCloseDetails?.();
                onCreate();
              }}
            />
          )}
        </>
      }
      filtersPanel={
        <DataTableFilters
          show={dt.showFilters}
          hasActiveFilters={hasActiveFilters}
          filterGroups={filterGroups}
          onClearFilters={clearFilters}
          resultCount={totalItems}
          resultLabel="club(s)"
          onToggleConfigure={
            dt.allowedFilterIds.length > 0 ? () => dt.setShowConfigure((v) => !v) : undefined
          }
          showConfigure={dt.showConfigure}
          configureContent={
            <FilterConfigurePanel
              allowedFilterIds={dt.allowedFilterIds}
              filterVisibility={dt.filterVisibility}
              filterGroupLabels={SPORT_CLUB_FILTER_GROUP_LABELS}
              onToggle={dt.handleToggleFilterVisibility}
              isSaving={dt.isSavingFilterVisibility}
            />
          }
        />
      }
      renderTableHead={() => (
        <tr>
          <th>Nom</th>
          <th>Type de sport</th>
          <th>Ville</th>
          <th>Site web</th>
          <th>Contact</th>
          <th className="w-28 text-center">Actions</th>
        </tr>
      )}
      renderTableRow={(club) => (
        <SportClubRow
          club={club}
          pageActions={pageActions}
          onView={resolvedView}
          onEdit={onEdit}
          onDelete={(id) => dt.setDeleteId(id)}
        />
      )}
      renderCard={(club) => (
        <SportClubCard
          club={club}
          pageActions={pageActions}
          onView={resolvedView}
          onEdit={onEdit}
          onDelete={(id) => dt.setDeleteId(id)}
        />
      )}
      emptyIcon={Trophy}
      emptyText="Aucun club de sport trouvé"
      title="Clubs de sport"
      sidebarMenuId="menu-sport-clubs"
      isSection={isSection}
      toolbarLeft={toolbarLeft}
      expandedRowId={dt.expandedRowId}
      expandedRowRef={dt.expandedRowRef}
      expandedCollapseRef={dt.expandedCollapseRef}
      onCloseDetails={onCloseDetails}
      expandedItem={expandedItem}
      isExpandedDetailLoading={isLoadingExpandedClub}
      renderExpandedRow={(club) => renderSportClubDetails(club)}
      renderDetailsContent={(club) => renderSportClubDetails(club)}
      modals={
        <DeleteModal
          open={!!dt.deleteId}
          onClose={() => dt.setDeleteId(null)}
          onConfirm={() => {
            if (dt.deleteId) deleteClubMutation.mutate(dt.deleteId);
          }}
          itemName={formatSportClubDeleteModalItemName(clubToDelete)}
          isLoading={deleteClubMutation.isPending}
        />
      }
    />
  );
};
